import axios, { AxiosInstance } from 'axios';
import { 
  LoginRequest, 
  LoginResponse, 
  User, 
  Project, 
  BindingTable, 
  TaskStatus, 
  UploadResponse,
  TypeClassificationTable
} from '../types';

// Determine API base URL for different environments.
// Priority: explicit env → PUBLIC_ fallback → derived (prod => /api, dev => :8000)
const deriveApiBaseUrl = (): string => {
  const explicit = process.env.REACT_APP_API_BASE_URL || (process.env as any).PUBLIC_API_BASE_URL;
  if (explicit) {
    return explicit;
  }

  const isLocalDev = window.location.hostname === 'localhost' || window.location.port === '3000';
  if (isLocalDev) {
    // CRA dev server on 3000 → backend on 8000
    const proto = window.location.protocol || 'http:';
    const host = window.location.hostname || 'localhost';
    return `${proto}//${host}:8000`;
  }

  // Production/default: same origin via reverse-proxy under /api
  return `${window.location.origin}/api`;
};

const API_BASE_URL = deriveApiBaseUrl();

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Добавляем интерцептор для добавления токена к запросам
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Интерцептор для обработки ошибок авторизации
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('access_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Авторизация
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const formData = new FormData();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await this.api.post<LoginResponse>('/auth/login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    // Сохраняем токен
    localStorage.setItem('access_token', response.data.access_token);
    
    return response.data;
  }

  async register(userData: { email: string; password: string }): Promise<User> {
    const response = await this.api.post<User>('/auth/register', userData);
    return response.data;
  }

  logout(): void {
    localStorage.removeItem('access_token');
  }

  // Проекты
  async getProjects(): Promise<Project[]> {
    const response = await this.api.get<Project[]>('/projects');
    return response.data;
  }

  async getProject(sessionId: string): Promise<Project> {
    const response = await this.api.get<Project>(`/projects/${sessionId}`);
    return response.data;
  }

  // Таблицы привязки
  async getBindingTable(sessionId: string, filePath: string): Promise<BindingTable> {
    const response = await this.api.get<BindingTable>(`/projects/${sessionId}/binding-table?file_path=${encodeURIComponent(filePath)}`);
    return response.data;
  }

  async saveBindingTable(sessionId: string, payload: { original_path: string; data: any[] }): Promise<any> {
    const response = await this.api.post(`/projects/${sessionId}/binding-table`, payload);
    return response.data;
  }

  // Таблицы классификации типов
  async getTypeTable(sessionId: string, pdfStem: string): Promise<TypeClassificationTable> {
    const response = await this.api.get<TypeClassificationTable>(`/type-classification/type-table/${sessionId}/${pdfStem}`);
    return response.data;
  }

  async saveTypeTable(sessionId: string, pdfStem: string, data: any[]): Promise<any> {
    const response = await this.api.post(`/type-classification/type-table/${sessionId}/${pdfStem}`, data);
    return response.data;
  }

  // Загрузка файлов
  async uploadPdf(files: File[]): Promise<UploadResponse> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await this.api.post<UploadResponse>('/files/upload-pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Статус задач
  async getTaskStatus(taskId: string): Promise<TaskStatus> {
    const response = await this.api.get<TaskStatus>(`/tasks/task-status/${taskId}`);
    return response.data;
  }

  // Генерация ВОР
  async generateVor(correctedTablePath: string): Promise<any> {
    const response = await this.api.post('/generate/generate-vor', {
      corrected_table_path: correctedTablePath,
    });
    return response.data;
  }

  // Получение ВОР как таблицы для отображения
  async getVorTable(sessionId: string, filePath: string): Promise<BindingTable> {
    const response = await this.api.get<BindingTable>(`/projects/${sessionId}/vor-table?file_path=${encodeURIComponent(filePath)}`);
    return response.data;
  }

  // Скачивание файлов
  async downloadFile(filePath: string): Promise<Blob> {
    const response = await this.api.post('/files/download', {
      file_path: filePath,
    }, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Получение URL для PDF файла
  getPdfUrl(sessionId: string, filename: string): string {
    return `${API_BASE_URL}/files/pdf/${sessionId}/${filename}`;
  }

  // Утилиты
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  getAuthToken(): string | null {
    return localStorage.getItem('access_token');
  }
}

export const apiService = new ApiService();
export default apiService; 